import { useCallback, useMemo, useState } from 'react'
import { Box, Button, IconButton, Paper, Tooltip } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import OutputOutlinedIcon from '@mui/icons-material/OutputOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'

import { ActionConfirmDialog } from '@/apps/latest/components/molecules/ActionConfirmDialog'
import { PatientInfoBar } from '@/apps/latest/components/molecules/PatientInfoBar'
import {
  PROCESSING_STEPS,
  ProcessingChecklist,
} from '@/apps/latest/components/molecules/ProcessingChecklist'
import { MetaText } from '@/components/atoms/MetaText'
import { SoapEditHistoryDialog } from '@/apps/latest/components/organisms/SoapEditHistoryDialog'
import { BulkReplaceDialog } from '@/apps/latest/components/organisms/BulkReplaceDialog'
import { useDetachNSIPSMutation } from '@/features/yakureki/hooks/queries/useSoapMutations'
import {
  usePatchCopyDoneMutation,
  usePostFeedbackMutation,
} from '@/features/yakureki'
import { useSoapWrite } from '@/apps/latest/features/yakureki/hooks/useSoapWrite'
import { useSendChatMessageMutation } from '@/features/formatter'
import { useRegenerateFromAudioMutation } from '@/apps/latest/features/yakureki/hooks/useRegenerateFromAudioMutation'
import { useRegenerateFromDictatedMutation } from '@/apps/latest/features/yakureki/hooks/useRegenerateFromDictatedMutation'
import { useToast } from '@/shared/contexts/ToastContext'
import { useReleaseChannel } from '@/features/auth'
import {
  useAnyCompassEmbed,
  usePatientMismatch,
  buildAnyCompassRows,
} from '@/features/anycompass'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { FONT_WEIGHT, UI_FONT_SIZE } from '@/shared/styles/typography'
import {
  isFailureStatus,
  type YakurekiDetail,
} from '@/shared/types/yakurekiCore'
import {
  SOAP_SECTION_KEYS,
  type SoapContent,
} from '@/shared/types/yakurekiSoap'
import { extractMedicationList } from '@/shared/utils/medicationHelper'
import { RADIUS } from '@/shared/styles/radii'

import {
  useGenerateTracingReportMutation,
  useTracingReportItemsQuery,
} from '@/features/internal/tracing-report'

import { SoapBody } from './SoapBody'
import { SoapMetaBlocks } from './SoapMetaBlocks'
import { HomeVisitReportPanel } from './HomeVisitReportPanel'
import { buildHomeVisitReportText } from './homeVisitFields'
import { HomeVisitPrintAction } from '@/apps/latest/features/home-visit/HomeVisitPrintButton'
import { MasterInsertAction } from './MasterInsertAction'
import {
  TracingReportPanel,
  buildTracingReportCopyText,
} from './TracingReportPanel'
import { PrescriptionReadingDialog } from './PrescriptionReadingDialog'
import { DeleteRecordingDialog } from './DeleteRecordingDialog'
import { FeedbackDialog } from './FeedbackDialog'
import { PostRecordingMoreMenu } from './PostRecordingMoreMenu'
import { RecordActionRow } from './RecordActionRow'
import { ConversationView } from './ConversationView'
import { buildSoapCopyText } from './soapCopyText'
import { MAX_REGENERATE, regenerateGate } from './regenerateGate'
import { changedSoapLines } from './changedSoapLines'
import { useCopyAndComplete, useCopyText } from './hooks/useSoapCopy'
import { useCopyShortcut } from './hooks/useCopyShortcut'
import { useRegenerateProgress } from './hooks/useRegenerateProgress'

interface Props {
  event: YakurekiDetail
  reloadEvent: () => Promise<void>
}

// AI で改善して変わった行に印を付けておく長さ (design-book citation.md の行 jump と同じ)
const FLASH_MS = 600
const NO_LINES = new Set<string>()

// SOAP 本体 / 会話 (文字起こし) / トレーシングレポート (外来・item 存在時) の view.
// SOAP が成果物で、会話は材料。資料は SOAP の下の節 (design-book 04-apps/kakeru.md「録音後の画面 — SOAP カードの構造」・ADR-0055).
type PostView = 'soap' | 'report' | 'conversation' | 'tracing'

// 録音後 (SOAP 生成後) の表示・編集・コピーを束ねる shell. SOAP 本体 / メタ群 / 会話ビュー
// /各 dialog を合成し、 view 切替・header・再生成・録音削除の orchestration を担う.
// 行の取消線/編集/追加など SOAP 編集の中核は SoapBody + hooks に分離している.
export const PostRecordingView = ({ event, reloadEvent }: Props) => {
  const { showToast } = useToast()
  // view 切替: SOAP / 会話 は共通、報告書は在宅のみ peer タブで常駐 (毎回必須の文書のため。
  // design-book ADR-0056 で SOAP の下からタブに戻した)。タブは α (左ナビ「在宅」と同じ段) だけで、
  // β は今の本番どおり SOAP の直下に見出しつきで出す (yakureki-back log/267)。
  // トレーシングレポートは item が存在するセッションに限り「毎回必須の文書」になるため、
  // その場合のみ peer タブを出す (該当なしセッションにはタブ自体が無い = エントロピーを足さない)。
  const [activeView, setActiveView] = useState<PostView>('soap')
  const isHomeVisit = event.session_type === 'home_visit'
  const { isAlpha } = useReleaseChannel()
  const showReportTab = isHomeVisit && isAlpha

  // トレーシングレポート (ルール検知 or 手動作成)。タブ出現の有無 = item の有無。
  const tracingItemsQuery = useTracingReportItemsQuery(event.id)
  const tracingItems = useMemo(
    () => tracingItemsQuery.data ?? [],
    [tracingItemsQuery.data]
  )
  const hasTracingItem = tracingItems.length > 0
  const generateTracingMutation = useGenerateTracingReportMutation(event.id)

  const { write } = useSoapWrite({ eventId: event.id })
  // 系統1: コピー = 完了 (mark-copied). 全文 master と 各 section のコピーで使う.
  const copyAndComplete = useCopyAndComplete(event.id)
  // 系統2 (clipboard のみ・完了させない): 訪問報告書の全体コピーに使う (SOAP の成果物完了とは別軸)。
  const copyText = useCopyText()
  // apps/latest 専用 hook (= 新 path: post_batch dictated → SOAP only). 完了済 SOAP に
  // 対しても再生成可能.
  const regenerateSoapMutation = useRegenerateFromDictatedMutation()
  const regenerateFromAudioMutation = useRegenerateFromAudioMutation()

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [bulkReplaceOpen, setBulkReplaceOpen] = useState(false)
  const [prescriptionReadingOpen, setPrescriptionReadingOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [regenerateConfirmOpen, setRegenerateConfirmOpen] = useState(false)
  // AI で改善して変わった行。600ms だけ印を付け、外して fade-out させる
  const [flashLines, setFlashLines] = useState<Set<string>>(NO_LINES)
  const [feedbackOpen, setFeedbackOpen] = useState(false)
  const medications = useMemo(() => extractMedicationList(event), [event])

  // 録音削除 (= legacy のゴミ箱と同挙動 = detach-nsips). audio / 文字起こし / SOAP を破棄し
  // event を PENDING に戻す (= 再録音可能). NSIPS 由来かつ再生成中でないときだけ可能.
  const detachMutation = useDetachNSIPSMutation()
  const canDeleteRecording =
    event.is_create_from_nsips && event.status !== 'processing'
  const handleDeleteRecording = useCallback(async () => {
    try {
      await detachMutation.mutateAsync(event.id)
      setDeleteConfirmOpen(false)
      showToast('録音を削除しました', 'success')
    } catch {
      showToast('録音の削除に失敗しました', 'error')
    }
  }, [event.id, detachMutation, showToast])

  // 改善依頼 (= この生成は変だった、を当社へ一言で送る)。画面に状態は残さず、送ったことは
  // toast でだけ伝える。失敗は dialog の中に出す (= 書いた文章を失わせない) ので、ここでは
  // 握り潰さずに throw を返す (yakureki-back log/231 D3・D4)。
  const postFeedbackMutation = usePostFeedbackMutation()
  const handleSubmitFeedback = useCallback(
    async (note: string) => {
      await postFeedbackMutation.mutateAsync({ yakurekiId: event.id, note })
      showToast('改善依頼を送りました', 'success')
    },
    [event.id, postFeedbackMutation, showToast]
  )

  // 採用 (is_selected) の行だけを clipboard へ. copy = 完了 (= section コピーと同じ
  // 系統1 useCopyAndComplete を使う).
  const handleCopy = useCallback(
    () => copyAndComplete(buildSoapCopyText(event.soaps)),
    [copyAndComplete, event.soaps]
  )

  // AnyCOMPASS iframe 埋め込み時のみ有効。生成 SOAP(コピーと同じ採用行)を親 React へ
  // postMessage で転記する(= 親の薬歴編集 state へフリー行として差し込む)。バックエンド
  // 保存はせず、親側の既存薬歴登録動線に委ねる。突合キーは INIT で受けた患者コンテキスト。
  const { isEmbedded, patientContext, deeplinkPatientName, applySoap } =
    useAnyCompassEmbed()
  // 突合ガード (赤帯 = PatientMismatchBanner) と同じ判定。不一致の間は転記を寸止めする。
  // 表示だけでは見落とすと別患者のカルテへ SOAP が入るため、終端操作側でも止める。
  const patientMismatch = usePatientMismatch(event.patient_name)
  // 転記も成果物を取り出した操作なのでコピーと同様に完了へ移す (mark-copied は冪等)。
  // 埋め込み運用では転記が終端操作であり、完了させないと患者一覧に残り続ける。
  const patchCopyDoneMutation = usePatchCopyDoneMutation()
  const handleTransfer = useCallback(async () => {
    // disabled ボタンと同条件の再チェック (深層防御)。呼び出し口が増えたときや
    // disabled の描画条件が崩れたときも、不一致中の転記はここで必ず止まる。
    if (patientMismatch) {
      showToast('患者が一致していないため転記できません', 'error')
      return
    }
    const rows = buildAnyCompassRows(event.soaps)
    if (rows.length === 0) {
      showToast('転記できる SOAP 行がありません', 'error')
      return
    }
    // 突合キーは SSoT (deeplink の患者名) を優先し、無ければ INIT で受けた氏名。
    // INIT 写しのエコーだけだと SSoT と INIT が割れたとき親ガードを通過する値が
    // 載ってしまう。親側はこの値と現在患者の氏名の一致で受理/破棄を決める。
    // 担当薬剤師名も送り、親側で調剤薬剤師へ氏名一致で反映させる(任意)。
    const ok = applySoap({
      patientKey: deeplinkPatientName ?? patientContext?.knjName ?? '',
      freeYkrkJskList: rows,
      pharmacistName: event.pharmacist_name ?? undefined,
    })
    if (!ok) {
      // pin 未成立 = 親へ届いていないので完了させない。
      showToast('転記先が確立していません', 'error')
      return
    }
    showToast('AnyCOMPASS へ転記・完了へ移動しました', 'info', 2000)
    await patchCopyDoneMutation.mutateAsync(event.id)
  }, [
    event.id,
    event.soaps,
    event.pharmacist_name,
    applySoap,
    deeplinkPatientName,
    patientContext,
    patientMismatch,
    patchCopyDoneMutation,
    showToast,
  ])

  // トレーシングレポートの全体コピー (= トレーシングレポートタブのヘッダー右上ボタン)。
  // 提出書類の転記元 (SOAP 本体の完了とは別軸) のため系統2 (完了させない)。
  const handleCopyTracingReport = useCallback(() => {
    void copyText(
      buildTracingReportCopyText(tracingItems),
      'トレーシングレポートをコピーしました'
    )
  }, [copyText, tracingItems])

  // 訪問報告書の全体コピー (= 報告書タブのヘッダー右上ボタン)。トレーシングと同じ系統2 (完了させない)。
  const handleCopyHomeVisitReport = useCallback(() => {
    void copyText(
      buildHomeVisitReportText(event.soaps),
      '訪問報告書をコピーしました'
    )
  }, [copyText, event.soaps])

  // ページ下部の行「トレーシングレポートを作成」。item が無ければ手動起点 item を作成して生成、
  // あれば再生成 (生成失敗時の再試行導線を兼ねる)。完了後はタブへ切り替える。
  const handleCreateTracingReport = useCallback(async () => {
    try {
      await generateTracingMutation.mutateAsync()
      setActiveView('tracing')
    } catch {
      // 失敗 toast は mutation onError が出す
    }
  }, [generateTracingMutation])

  // Ctrl+Shift+C で「全 SOAP コピー → 完了へ移動」。
  useCopyShortcut(handleCopy)

  // 印を付ける行は、送る前の SOAP (この render の event.soaps) と返ってきた SOAP の差で決める。
  // 自動で scroll はしない (目線がぶれる)。資料の節から反映したときは toast で済んだことを知らせる
  const handleAiUpdate = useCallback(
    async (updatedSoap: SoapContent) => {
      const changed = changedSoapLines(event.soaps, updatedSoap)
      await reloadEvent()
      setFlashLines(changed)
      window.setTimeout(() => setFlashLines(NO_LINES), FLASH_MS)
    },
    [event.soaps, reloadEvent]
  )

  // 資料の節の ⓘ = まだ SOAP に入っていない資料だけを読む、文章なしの差分更新 (手で直した行は残る)。
  // 済んだら変わった行を点滅
  const { mutateAsync: sendChatMessage } = useSendChatMessageMutation()
  const handleReflect = useCallback(
    async (ids: string[]) => {
      const res = await sendChatMessage({
        yakurekiId: event.id,
        message: '',
        inputType: 'text',
        attachmentIds: ids,
      })
      await handleAiUpdate(res.updated_soap)
      showToast('資料を反映しました', 'success')
    },
    [event.id, handleAiUpdate, sendChatMessage, showToast]
  )

  const handleRegenerate = useCallback(async () => {
    // toast / event reload は mutation の onSuccess/onError が SSoT で出すため、 caller では
    // 握り潰す (= 2 重 reload を避ける).
    try {
      await regenerateSoapMutation.mutateAsync(event.id)
    } catch {
      // 失敗時の toast は mutation onError が出す (= caller では何もしない)
    }
  }, [event.id, regenerateSoapMutation])

  const regenerateCount = event.regenerate_count ?? 0
  const { canRegenerate, tooltip: regenerateTooltip } = regenerateGate(event)

  // FAILED 復旧導線: 保存済み音声から transcribe → SOAP をやり直す (旧 path).
  // failed_short (短音声) は再生成しても回復しないため対象外.
  const canRegenerateFromAudio =
    event.status === 'failed' && Boolean(event.audio?.audio)
  const handleRegenerateFromAudio = useCallback(async () => {
    try {
      await regenerateFromAudioMutation.mutateAsync(event.id)
    } catch {
      // 失敗時の toast は mutation onError が出す (= caller では何もしない)
    }
  }, [event.id, regenerateFromAudioMutation])

  // 再生成中表示: ratchet で isPostPhase=true で本 view に到達してる時の
  // event.status='processing' は **必ず再生成中** (= 初回生成は本 view に到達不可).
  const isRegenerating = event.status === 'processing'
  const regenStep = useRegenerateProgress(isRegenerating)

  // MedicationPlaceholderText / BulkReplaceDialog の onReplace. replaceAll=true で全 section
  // 置換、 false で最初の 1 件のみ置換.
  const handlePlaceholderReplace = useCallback(
    async (
      placeholder: string,
      replacement: string,
      replaceAll?: boolean
    ): Promise<boolean> => {
      // 置換元は cache の最新から取る (props の閉包から組むと、往復中の別編集を落とす)。
      // 走査対象は SOAP 6 section のみ: sidecar (followup_plans / physician_message) も
      // 配列値を持つが back の SOAP 更新は section しか読まないため、触っても捨てられる。
      return write((current) => {
        const { id: _id, ...rest } = current
        const updated = { ...rest } as SoapContent
        let replacedOnce = false
        SOAP_SECTION_KEYS.forEach((key) => {
          const items = updated[key]
          if (!Array.isArray(items)) return
          updated[key] = items.map((item) => {
            if (replaceAll) {
              return {
                ...item,
                content: item.content.split(placeholder).join(replacement),
              }
            }
            if (!replacedOnce && item.content.includes(placeholder)) {
              replacedOnce = true
              return {
                ...item,
                content: item.content.replace(placeholder, replacement),
              }
            }
            return item
          })
        })
        return updated
      }, 'bulk_replace').then(async (ok) => {
        if (ok) await reloadEvent()
        return ok
      })
    },
    [write, reloadEvent]
  )

  // SOAP / 会話 / 提出書類 の segmented control. 各セグメントが独立 button で、押下で
  // その view に切替える. active 側だけ地色を付けて現在 view を示す.
  const segmentLabelSx = (isActive: boolean) =>
    ({
      appearance: 'none',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'inherit',
      display: 'inline-flex',
      alignItems: 'center',
      // 狭い幅 (スマホ) で segment が増えると文字が折れるので、折らずに tablist の中で横に scroll する
      whiteSpace: 'nowrap',
      flexShrink: 0,
      height: 28,
      px: 1.75,
      borderRadius: `${RADIUS.full}px`,
      fontSize: UI_FONT_SIZE.label,
      fontWeight: FONT_WEIGHT.medium,
      bgcolor: isActive ? colorTokens.bg.default : 'transparent',
      color: isActive ? colorTokens.fg.default : colorTokens.fg.muted,
      transition: 'background-color 120ms, color 120ms',
      '&:focus': { outline: 'none' },
      '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
    } as const)

  const viewSegments: { key: PostView; label: string }[] = [
    { key: 'soap', label: 'SOAP' },
    // 訪問報告書は在宅のみの peer タブ (SOAP の下に資料・フォローアップ計画と並べると、どこまでが
    // 報告書か見分けにくい。宛先ごとに刷る 1 つの文書。design-book ADR-0056)。
    ...(showReportTab ? [{ key: 'report' as PostView, label: '報告書' }] : []),
    { key: 'conversation', label: '会話' },
    // 資料は SOAP の下の節 (header の view にした決定 43 は同日夜の決定 44・45 で戻した)。
    // トレーシングレポートは item が存在するセッションのみ (= 提出書類があるセッションでは
    // 毎回必須なので peer view。無いセッションにはタブ自体を出さない)。
    // ラベルは「トレーシング」に短縮 (在宅報告書→報告書と同じタブ行文法。
    // フルネームはパネル見出しが担う)。
    ...(hasTracingItem
      ? [{ key: 'tracing' as PostView, label: 'トレーシング' }]
      : []),
  ]

  const ViewSwitch = (
    <Box
      role="tablist"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        p: 0.25,
        gap: 0.25,
        borderRadius: `${RADIUS.full}px`,
        bgcolor: colorTokens.bg.muted,
        // 入り切らない幅では横に scroll (scrollbar は出さない)。右の ⧉・⋯ は縮めない
        minWidth: 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {viewSegments.map((seg) => (
        <Box
          key={seg.key}
          component="button"
          role="tab"
          aria-selected={activeView === seg.key}
          aria-label={`${seg.label}を表示`}
          onClick={() => setActiveView(seg.key)}
          sx={segmentLabelSx(activeView === seg.key)}
        >
          {seg.label}
        </Box>
      ))}
    </Box>
  )

  // ⋯ menu (= 一括置換 / 再生成 / 録音削除) を header right に置く共通 button.
  const moreMenuButton = (
    <Tooltip title="その他">
      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  )

  // header の右 slot. コピー帯は廃止し、 静かな copy アイコン 1 つ + ⋯ のみ.
  // SOAP 表示中は master copy (= 全文コピー + 完了へ移動). 会話 / 提出書類 表示中は
  // copy アイコンを置かず、 各見出しクリックでコピーする (= 「見出し = コピー」に統一).
  const headerRightSlot =
    activeView === 'soap' ? (
      <>
        {/* AnyCOMPASS 埋め込み時のみ: 生成 SOAP を親薬歴へ転記. */}
        {isEmbedded && (
          <Tooltip
            title={
              patientMismatch
                ? '患者が一致していないため転記できません'
                : 'AnyCOMPASS へ転記 (完了へ移動)'
            }
          >
            <span>
              <IconButton
                size="small"
                onClick={() => void handleTransfer()}
                disabled={isRegenerating || patientMismatch}
              >
                <OutputOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {/* 埋め込み時は転記が同じ内容をホストへ渡すため全文コピーは出さない
            (design-book 03-patterns/embedded-shell.md)。完了への遷移は各見出し
            クリックと Ctrl+Shift+C で担保される。 */}
        {!isEmbedded && (
          <Tooltip title="コピー (完了へ移動)">
            <span>
              <IconButton
                size="small"
                onClick={() => void handleCopy()}
                disabled={isRegenerating}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        {moreMenuButton}
      </>
    ) : activeView === 'report' ? (
      // 報告書タブ: トレーシングと同型 (全体コピーは系統2・完了させない) に、宛先ごとの印刷を足す。
      <>
        <HomeVisitPrintAction event={event} />
        <Tooltip title="訪問報告書をすべてコピー">
          <span>
            <IconButton
              size="small"
              onClick={() => void handleCopyHomeVisitReport()}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {moreMenuButton}
      </>
    ) : activeView === 'tracing' ? (
      // トレーシングレポートタブ: 報告書と同型 (全体コピーは系統2・完了させない)。
      <>
        <Tooltip title="トレーシングレポートをすべてコピー">
          <span>
            <IconButton
              size="small"
              onClick={() => void handleCopyTracingReport()}
            >
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {moreMenuButton}
      </>
    ) : (
      moreMenuButton
    )

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <PatientInfoBar event={event} />
      </Box>
      <Paper
        variant="outlined"
        sx={{ borderRadius: `${RADIUS.xl}px`, overflow: 'hidden' }}
      >
        {/* header は SOAP / 会話 の segmented view switch + 静かな copy + ⋯ のみ. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.75,
            borderBottom: `1px solid ${colorTokens.border.default}`,
            minHeight: 44,
          }}
        >
          {ViewSwitch}
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, flexShrink: 0 }}>
            {headerRightSlot}
          </Box>
        </Box>
        <Box sx={{ p: 2, minHeight: 200 }}>
          {activeView === 'soap' && (
            <>
              {isFailureStatus(event.status) && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: `${RADIUS.md}px`,
                    bgcolor: colorTokens.overlay.hover,
                  }}
                >
                  <MetaText>
                    {event.status === 'failed_short'
                      ? '録音時間が短すぎるため、文字起こしができませんでした。'
                      : '服薬指導会話が正確に認識できませんでした。'}
                  </MetaText>
                  {canRegenerateFromAudio && (
                    <Box sx={{ mt: 1 }}>
                      <Tooltip
                        title={
                          regenerateCount >= MAX_REGENERATE
                            ? `再生成回数の上限 (${MAX_REGENERATE} 回) に達しました`
                            : '保存済みの音声から文字起こしと SOAP 生成をやり直します'
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            startIcon={<RefreshIcon />}
                            onClick={() => void handleRegenerateFromAudio()}
                            disabled={
                              regenerateFromAudioMutation.isPending ||
                              regenerateCount >= MAX_REGENERATE
                            }
                          >
                            {regenerateFromAudioMutation.isPending
                              ? '再生成を開始しています…'
                              : '音声から再生成'}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              )}
              {isRegenerating ? (
                <ProcessingChecklist
                  steps={PROCESSING_STEPS}
                  currentIdx={regenStep}
                  allDone={false}
                />
              ) : (
                <>
                  <SoapBody
                    event={event}
                    reloadEvent={reloadEvent}
                    medications={medications}
                    flashLines={flashLines}
                    onPlaceholderReplace={handlePlaceholderReplace}
                  />
                  {/* 定型文 (SOAPセット / 文章マスタ) の挿入。AnyCOMPASS 埋め込み中かつ
                      マスタがあるときだけ出る (中で自己判定するのでここでは条件を書かない). */}
                  <MasterInsertAction event={event} reloadEvent={reloadEvent} />
                  {isHomeVisit && !showReportTab && (
                    <HomeVisitReportPanel
                      event={event}
                      onCopyAll={handleCopyHomeVisitReport}
                    />
                  )}
                  <SoapMetaBlocks
                    event={event}
                    reloadEvent={reloadEvent}
                    onAiUpdate={handleAiUpdate}
                    onReflect={handleReflect}
                  />
                </>
              )}
            </>
          )}
          {activeView === 'report' && showReportTab && (
            <HomeVisitReportPanel event={event} />
          )}
          {activeView === 'conversation' && <ConversationView event={event} />}
          {/* トレーシングレポート view は item 存在時のみの peer tab。ルール検知 (NSIPS 受信時)
              またはページ下部の行の手動作成で item が生まれる。 */}
          {activeView === 'tracing' && hasTracingItem && (
            <TracingReportPanel event={event} items={tracingItems} />
          )}
        </Box>
      </Paper>

      {/* ページ下部の二次アクションの行 (編集履歴 / 処方を読む β / トレーシング β / 改善依頼)。
          SOAP を読み終えた位置に置く。表示中の view に依らず出す (記録 1 件への操作のため)。 */}
      <RecordActionRow
        onHistory={() => setHistoryDialogOpen(true)}
        onPrescriptionReading={() => setPrescriptionReadingOpen(true)}
        onCreateTracingReport={() => void handleCreateTracingReport()}
        isHomeVisit={isHomeVisit}
        hasTracingItem={hasTracingItem}
        isTracingPending={generateTracingMutation.isPending}
        onFeedback={() => setFeedbackOpen(true)}
      />

      {/* ⋯ menu (= 一括置換 / 再生成 / 録音削除). */}
      <PostRecordingMoreMenu
        anchorEl={menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onBulkReplace={() => {
          setMenuAnchor(null)
          setBulkReplaceOpen(true)
        }}
        onRegenerate={() => {
          setMenuAnchor(null)
          setRegenerateConfirmOpen(true)
        }}
        canRegenerate={canRegenerate}
        regenerateTooltip={regenerateTooltip}
        isRegeneratePending={regenerateSoapMutation.isPending}
        canDeleteRecording={canDeleteRecording}
        onDeleteRecording={() => {
          setMenuAnchor(null)
          setDeleteConfirmOpen(true)
        }}
      />

      {/* 再生成の確認。手で直した行も新しい版で置き換わるので、押す前に 1 行で知らせる. */}
      <ActionConfirmDialog
        open={regenerateConfirmOpen}
        title="SOAP を再生成"
        body="会話と資料から作り直します。手で直した行も置き換わります。"
        actionLabel="再生成"
        onClose={() => setRegenerateConfirmOpen(false)}
        onConfirm={() => {
          setActiveView('soap')
          void handleRegenerate()
        }}
      />

      {/* 改善依頼 (= ページ下部の行から開く 1 欄の dialog)。送信で閉じて toast。 */}
      <FeedbackDialog
        open={feedbackOpen}
        onClose={() => setFeedbackOpen(false)}
        onSubmit={handleSubmitFeedback}
      />

      {/* 録音削除の確認 dialog. 取り消せない破壊的操作のため明示確認 + 削除は danger 色. */}
      <DeleteRecordingDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onDelete={() => void handleDeleteRecording()}
        isPending={detachMutation.isPending}
      />

      {/* 処方の読み (Kakeru Education・ティーチングモード): ページ下部の行から開く中央ダイアログ。
          自分の読みを書いて提出すると AI がその読みへ講評を返す学習フロー。 */}
      <PrescriptionReadingDialog
        open={prescriptionReadingOpen}
        onClose={() => setPrescriptionReadingOpen(false)}
        event={event}
      />

      <SoapEditHistoryDialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        yakurekiId={event.id}
      />
      <BulkReplaceDialog
        open={bulkReplaceOpen}
        onClose={() => setBulkReplaceOpen(false)}
        soapContent={event.soaps ?? undefined}
        medications={medications}
        onReplace={async (searchText, replaceText) => {
          const ok = await handlePlaceholderReplace(
            searchText,
            replaceText,
            true
          )
          if (!ok) {
            showToast('一括置換に失敗しました', 'error')
            throw new Error('bulk replace failed')
          }
          showToast('一括置換しました', 'success')
        }}
      />
    </Box>
  )
}
